import type { AgencyVisibility } from '@saaga/shared/interfaces/agency.interface'
import type { ReactNode } from 'react'

import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'

interface AgencyVisibilityBadgeProps {
  visibility: AgencyVisibility
  className?: string
}

interface VisibilityConfig {
  label: string
  className: string
}

const PENDING_REVIEW: AgencyVisibility = 'pending_review'

const visibilityConfigMap: Record<AgencyVisibility, VisibilityConfig> = {
  draft: {
    label: 'Draft',
    className: 'bg-muted text-muted-foreground hover:bg-muted',
  },
  private: {
    label: 'Private',
    className:
      'bg-blue-100 text-blue-800 hover:bg-blue-100 dark:bg-blue-900 dark:text-blue-300 dark:hover:bg-blue-900',
  },
  [PENDING_REVIEW]: {
    label: 'Pending Review',
    className:
      'bg-yellow-100 text-yellow-800 hover:bg-yellow-100 dark:bg-yellow-900 dark:text-yellow-300 dark:hover:bg-yellow-900',
  },
  public: {
    label: 'Public',
    className:
      'bg-green-100 text-green-800 hover:bg-green-100 dark:bg-green-900 dark:text-green-300 dark:hover:bg-green-900',
  },
  rejected: {
    label: 'Rejected',
    className:
      'bg-red-100 text-red-800 hover:bg-red-100 dark:bg-red-900 dark:text-red-300 dark:hover:bg-red-900',
  },
}

/**
 * Displays the current visibility state of an agency as a colored badge.
 */
export const AgencyVisibilityBadge = ({
  visibility,
  className,
}: AgencyVisibilityBadgeProps): ReactNode => {
  const config = visibilityConfigMap[visibility]

  return (
    <Badge
      variant="secondary"
      className={cn('agency-visibility-badge', config.className, className)}
    >
      {config.label}
    </Badge>
  )
}

AgencyVisibilityBadge.displayName = 'AgencyVisibilityBadge'
